import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import { useSnackbar } from "notistack";
import DocumentViewer from "../MDDForm/Viewer/DocumentViewer.jsx";
import { Form, FormRenderer } from "../MDDForm/MDDForm.jsx";
import { getRWObjectCopy } from "../MDDForm/helper.js";
import { fedfUploadAnlage } from "formdefs/upload_anlage.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";

import { Backdrop, CircularProgress, Button } from "@mui/material";

import { useClaimAttachmentActions } from "../../hooks/useClaimAttachmentActions.jsx";

function AttachmentModal({ open, onClose, attachment_id, schaden_id }) {
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const { user } = useUser();

  const { createClaimAttachments, abort, findById } =
    useClaimAttachmentActions();

  // Bleibt so lange gemountet, bis der Viewer-Dialog seine Schließanimation
  // abgeschlossen hat (siehe handleAttachmentViewerExited) - andernfalls wird
  // der MUI-Dialog mitten in der Transition unmountet und ein Overlay kann
  // hängen bleiben.
  const isShowAttachmentViewer = !!attachment;

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    if (open && attachment_id) {
      // Nur ausführen, wenn die aktuelle attachment_id nicht bereits geladen ist
      if (!attachment || String(attachment.id) !== String(attachment_id)) {
        findById(attachment_id, {
          setIsLoading,
          enqueueSnackbar,
          setResult: (data) => {
            const attachment_data = data?.data ?? data;
            setAttachment({
              ...attachment_data,
              success: true,
            });
            setOpenCD(true);
          },
        });
      }
    } else if (open && !attachment_id) {
      setAttachment(null);
      if (schaden_id) {
        backendGet(`data/anlages/used-in-tags?schaden_id=${schaden_id}`, {
          setIsLoading: () => {},
          setResult: (res) => {
            setOpenCD(true);
            showUploadForm(res?.data ?? []);
          },
        });
      } else {
        setOpenCD(true);
        showUploadForm([]);
      }
    }
    // eslint-disable-next-line
  }, [open, attachment_id]);

  const showUploadForm = (usedInSuggestions = []) => {
    const fdef = getRWObjectCopy(fedfUploadAnlage);
    fdef.formdefinition[0].used_in_suggestions = usedInSuggestions;
    fdef.formdefinition = fdef.formdefinition.filter(
      (item) =>
        user.role_names.includes("sysadmin") ||
        user.role_names.includes("accredo") ||
        item.name !== "intern"
    );
    setTca({
      title: "Anlage hochladen",
      maxWidth: "lg",
      content: (
        <Form>
          <FormRenderer
            fdef={fdef}
            model={{ schaden_id: schaden_id }}
            variant="filled"
            editMode={true}
            showActions={true}
            showCancel={true}
            showToggleEdit={false}
            actionsPosition="bottom"
            toggleEditPosition="top"
            showErrorText={false}
            cancelAction={onClose}
            submitAction={{
              method: "callback",
              callback: async (formData) => {
                await createClaimAttachments(formData, {
                  setIsLoading,
                  onSuccess: onClose,
                });
              },
            }}
          />
        </Form>
      ),
      actions: null,
    });
  };

  const handleAbort = () => {
    abort();
  };

  const handleAttachmentViewerExited = () => {
    setAttachment(null);
  };

  return (
    <>
      <Backdrop
        open={isLoading}
        style={{ zIndex: 1300, flexDirection: "column" }}
      >
        <CircularProgress style={{ marginBottom: 16 }} />
        <Button variant="contained" color="secondary" onClick={handleAbort}>
          Abbrechen
        </Button>
      </Backdrop>
      {isShowAttachmentViewer ? (
        <DocumentViewer
          document={attachment}
          open={open}
          onClose={onClose}
          onExited={handleAttachmentViewerExited}
        />
      ) : (
        <ContentDialog
          openCD={openCD}
          setOpenCD={setOpenCD}
          tca={tca}
          onClose={onClose}
        />
      )}
    </>
  );
}

AttachmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attachment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  schaden_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AttachmentModal;
